import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as providerService from "@/shared/api/services/providerService";
import { motion } from "framer-motion";
import {
  Server,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  ArrowDownToLine,
  ArrowUpFromLine,
  QrCode,
} from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

// statusConfig will be created inside component to use translations

const typeColors = {
  bank: "bg-blue-500",
  ewallet: "bg-purple-500",
  qr: "bg-emerald-500",
  card: "bg-amber-500",
};

export default function MerchantProviders() {
  const [search, setSearch] = useState("");
  const { language, t } = useLanguage();

  const statusConfig = {
    active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("active") },
    inactive: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("inactive") },
    maintenance: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("maintenance") },
    degraded: { icon: AlertTriangle, color: "bg-orange-100 text-orange-700", label: t("degraded") },
  };

  // Helper function to translate provider status
  const translateStatus = (status) => {
    if (!status) return t("na") || "N/A";
    const statusLower = String(status).toLowerCase().trim();
    const statusMap = {
      "active": t("active"),
      "inactive": t("inactive"),
      "degraded": t("degraded"),
      "maintenance": t("maintenance"),
    };
    return statusMap[statusLower] || status;
  };

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const filteredProviders = providers.filter(p => {
    const providerName = getProviderName(p, language);
    return providerName?.toLowerCase().includes(search.toLowerCase()) ||
      p.code?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <MerchantLayout currentPage="MerchantProviders" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{t("providers")}</h1>
            <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
              {t("viewOnly")}
            </Badge>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchProviders")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Providers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProviders.map((provider, index) => {
            const statusKey = (provider.status || '').toLowerCase();
            const status = statusConfig[statusKey] || statusConfig.active;
            const StatusIcon = status.icon;

            return (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-5 shadow-sm border border-slate-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${typeColors[provider.type] || "bg-slate-500"} flex items-center justify-center`}>
                      <Server className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{getProviderName(provider, language)}</h3>
                      <p className="text-xs text-slate-500">{provider.code}</p>
                    </div>
                  </div>
                  <Badge className={status.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{t("type")}</span>
                    <span className="font-medium capitalize">{provider.type}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{t("successRate")}</span>
                    <span className="font-medium text-emerald-600">{provider.success_rate || 100}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{t("avgResponse")}</span>
                    <span className="font-medium">{provider.avg_response_time || 0}ms</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                  {provider.supports_deposit && (
                    <Badge variant="outline" className="text-xs">
                      <ArrowDownToLine className="h-3 w-3 mr-1" />
                      {t("deposits")}
                    </Badge>
                  )}
                  {provider.supports_withdrawal && (
                    <Badge variant="outline" className="text-xs">
                      <ArrowUpFromLine className="h-3 w-3 mr-1" />
                      {t("withdrawals")}
                    </Badge>
                  )}
                  {provider.supports_qr && (
                    <Badge variant="outline" className="text-xs">
                      <QrCode className="h-3 w-3 mr-1" />
                      QR
                    </Badge>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredProviders.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Server className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>{t("noProvidersFound")}</p>
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}